import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import { URL_LANGS } from "@/lib/langRoutes";
import SEO from "@/lib/seoPages.json";

// Per-language, per-page technical/on-page SEO for this single-page app:
// title, meta description, canonical (self-referencing), robots, Open Graph,
// and reciprocal hreflang alternates (it/en/es/pt-BR + x-default -> Italian).
// Does NOT touch any visible UI, texts, components or i18n content.
const SITE = "https://ananawi.com";
const LMAP = Object.fromEntries(URL_LANGS.map((l) => [l.code, l]));
const HREFLANG_ORDER = ["it", "en", "es", "pt"];

// Public, indexable pages (base path = Italian slug): see lib/seoPages.json (also used by the postbuild).

const { PAGES, PRIVATE } = SEO;
const OG_IMAGE = `${SITE}/og/ananawi-og.jpg`;
const NOINDEX_PUBLIC_PAGES = new Set(["/termini"]);


const loc = (prefix, basePath) => `${SITE}${prefix}${basePath === "/" ? "/" : basePath}`;

const setMeta = (key, keyValue, content) => {
  let el = document.head.querySelector(`meta[${key}="${keyValue}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(key, keyValue);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setCanonical = (href) => {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
};

const setAlternates = (alternates) => {
  // Pages ship static hreflang tags for crawlers; replace them after hydration
  // instead of appending a second, identical set.
  document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((e) => e.remove());
  alternates.forEach((a) => {
    const l = document.createElement("link");
    l.setAttribute("rel", "alternate");
    l.setAttribute("hreflang", a.hreflang);
    l.setAttribute("href", a.href);
    document.head.appendChild(l);
  });
};

export const CanonicalSync = () => {
  const { pathname } = useLocation(); // relative to basename (prefix already stripped)
  const { lang } = useI18n();
  useEffect(() => {
    const info = LMAP[lang] || LMAP.it;
    const prefix = info.prefix;
    const basePath = pathname === "/" ? "/" : (pathname.replace(/\/+$/, "") || "/");

    let title;
    let description;
    let ogTitle;
    let robots;
    let canonical = loc(prefix, basePath);
    let alternates = [];

    const page = PAGES[basePath];
    const priv = PRIVATE[basePath];

    if (page) {
      const L = page[lang] || page.it;
      title = L.t;
      description = L.d;
      ogTitle = L.og || L.t;
      robots = NOINDEX_PUBLIC_PAGES.has(basePath) ? "noindex, follow" : "index, follow";
      alternates = HREFLANG_ORDER.map((code) => ({
        hreflang: LMAP[code].hreflang,
        href: loc(LMAP[code].prefix, basePath),
      }));
      alternates.push({ hreflang: "x-default", href: loc("", basePath) });
    } else if (priv) {
      title = priv[lang] || priv.it;
      description = PAGES["/"][lang]?.d || PAGES["/"].it.d;
      ogTitle = title;
      robots = priv.robots;
      alternates = []; // non-indexable: no hreflang
    } else {
      // Unknown path: not a real page; never allow indexing of the SPA fallback.
      const L = PAGES["/"][lang] || PAGES["/"].it;
      title = L.t;
      description = L.d;
      ogTitle = L.og || L.t;
      robots = "noindex, follow";
      canonical = loc(prefix, "/");
      alternates = HREFLANG_ORDER.map((code) => ({
        hreflang: LMAP[code].hreflang,
        href: loc(LMAP[code].prefix, "/"),
      }));
      alternates.push({ hreflang: "x-default", href: loc("", "/") });
    }

    document.title = title;
    setMeta("name", "description", description);
    setMeta("name", "robots", robots);
    setMeta("property", "og:title", ogTitle);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", canonical);
    setMeta("property", "og:locale", info.ogLocale);
    setMeta("property", "og:image", OG_IMAGE);
    setMeta("name", "twitter:title", ogTitle);
    setMeta("name", "twitter:description", description);
    document.documentElement.lang = info.htmlLang || "it";
    setCanonical(canonical);
    setAlternates(alternates);
  }, [pathname, lang]);

  return null;
};
